import React, { useState, useEffect, useRef } from 'react';
import { TfiAnnouncement } from 'react-icons/tfi';
import { IoClose, IoChevronForward } from 'react-icons/io5';
import { HiOutlineBell } from 'react-icons/hi';
import axios from 'axios';
import './UnseenAnnouncementsDialog.css';

const UnseenAnnouncementsDialog = ({ show, onClose, onNavigateToAnnouncements }) => {
  const [unseenAnnouncements, setUnseenAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [soundPlayed, setSoundPlayed] = useState(false);
  const audioRef = useRef(null);
  const userId = parseInt(localStorage.getItem('userId'));
  const userRole = localStorage.getItem('role') || 'All';
  const userDepartment = localStorage.getItem('department');

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio('/sounds/notification.mp3');
    audioRef.current.preload = 'auto';
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Play sound when dialog opens and has announcements
  useEffect(() => {
    if (show && userId && !loading && unseenAnnouncements.length > 0 && !soundPlayed) {
      playNotificationSound();
      setSoundPlayed(true);
    }
    
    if (!show) {
      setSoundPlayed(false);
    }
  }, [show, unseenAnnouncements, loading, userId, soundPlayed]);

  const playNotificationSound = async () => {
    if (!audioRef.current) return;
    
    try {
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
      console.log('🔔 Notification sound played successfully');
    } catch (error) {
      console.log('Unable to play notification sound:', error);
    }
  };

  const fetchUnseenAnnouncements = async () => {
    if (!userId) return;
    
    setLoading(true);
    let allAnnouncements = [];

    try {
      // Fetch role-based announcements
      const regularRoleResponse = await axios.get('https://backend.ems.hiradentallab.com/api/announcements/role/announcement', {
        params: { role: userRole, userId: userId }
      });
      
      if (regularRoleResponse.data && regularRoleResponse.data.length > 0) {
        const roleAnnouncements = regularRoleResponse.data.map(item => ({ 
          ...item, 
          type: 'regular', 
          source: 'role' 
        }));
        allAnnouncements.push(...roleAnnouncements);
      }

      // Fetch user-based announcements
      const regularUserResponse = await axios.get(`https://backend.ems.hiradentallab.com/api/user/announcements/user/${userId}`);
      
      if (regularUserResponse.data && regularUserResponse.data.length > 0) {
        const userAnnouncements = regularUserResponse.data.map(item => ({ 
          ...item, 
          type: 'regular', 
          source: 'user' 
        }));
        allAnnouncements.push(...userAnnouncements);
      }

      // Fetch department announcements
      if (userDepartment && userDepartment !== 'N/A') {
        try {
          const deptResponse = await axios.get('https://backend.ems.hiradentallab.com/api/deptannouncements/by-department', {
            params: { department: userDepartment, userId: userId }
          });
          
          if (deptResponse.data && deptResponse.data.length > 0) {
            const deptAnnouncements = deptResponse.data.map(item => ({ 
              ...item, 
              type: 'department', 
              source: 'department' 
            }));
            allAnnouncements.push(...deptAnnouncements);
          }
        } catch (deptError) {
          console.log('Department announcements API not available');
        }
      }

      // Remove duplicates
      const uniqueAnnouncements = allAnnouncements.filter((item, index, self) =>
        self.findIndex(t => t._id === item._id) === index
      );

      // Filter unseen announcements
      const unseen = uniqueAnnouncements.filter(announcement => {
        const hasSeen = announcement.seenBy?.some(seen => seen.userId === userId);
        return !hasSeen;
      });

      // Sort by date (newest first)
      const sortedUnseen = unseen.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setUnseenAnnouncements(sortedUnseen);
      
      if (sortedUnseen.length === 0 && show) {
        setTimeout(() => {
          onClose();
        }, 500);
      }
      
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && userId) {
      fetchUnseenAnnouncements();
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [show, userId]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const handleAnnouncementClick = (announcement) => {
    setIsVisible(false);
    setTimeout(() => {
      // Pass the announcement to navigate to
      onNavigateToAnnouncements(announcement);
      onClose();
    }, 300);
  };

  const handleViewAll = () => {
    setIsVisible(false);
    setTimeout(() => {
      // Navigate to announcements page without any specific announcement
      onNavigateToAnnouncements(null);
      onClose();
    }, 300);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getAnnouncementIcon = (announcement) => {
    if (announcement.type === 'department') return '🏢';
    if (announcement.source === 'user') return '👤';
    return '📢';
  };

  if (!show) return null;

  return (
    <>
      <div className={`notification-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose} />
      <div className={`notification-dialog ${isVisible ? 'visible' : ''}`}>
        <div className="notification-header">
          <div className="notification-header-left">
            <div className="notification-icon-wrapper">
              <HiOutlineBell className="notification-bell-icon" />
              {!loading && unseenAnnouncements.length > 0 && (
                <span className="notification-badge">{unseenAnnouncements.length}</span>
              )}
            </div>
            <div className="notification-title-section">
              <h3 className="notification-title">Notifications</h3>
              <p className="notification-subtitle">
                {loading 
                  ? 'Checking for updates...' 
                  : unseenAnnouncements.length === 0 
                    ? 'No new announcements' 
                    : `You have ${unseenAnnouncements.length} new ${unseenAnnouncements.length === 1 ? 'announcement' : 'announcements'}`}
              </p>
            </div>
          </div>
          <button className="notification-close-btn" onClick={handleClose}>
            <IoClose size={22} />
          </button>
        </div>

        <div className="notification-body">
          {loading ? (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              <span>Loading announcements...</span>
            </div>
          ) : unseenAnnouncements.length === 0 ? (
            <div className="notification-empty">
              <div className="empty-icon">✓</div>
              <h4>All Caught Up!</h4>
              <p>No new announcements to show</p>
            </div>
          ) : (
            <div className="notification-list">
              {unseenAnnouncements.slice(0, 5).map((announcement) => (
                <div
                  key={`${announcement.type}-${announcement._id}`}
                  className="notification-item"
                  onClick={() => handleAnnouncementClick(announcement)}
                >
                  <div className="notification-item-icon">
                    {getAnnouncementIcon(announcement)}
                  </div>
                  <div className="notification-item-content">
                    <div className="notification-item-header">
                      <h4 className="notification-item-title">{announcement.title}</h4>
                      <span className="notification-item-time">{formatTimeAgo(announcement.createdAt)}</span>
                    </div>
                    <p className="notification-item-preview">
                      {(announcement.message || announcement.content || "").substring(0, 80)}
                      {(announcement.message || announcement.content || "").length > 80 ? "..." : ""}
                    </p>
                    <div className="notification-item-meta">
                      <span className="meta-badge">
                        {announcement.type === 'department' 
                          ? announcement.department 
                          : announcement.source === 'user' 
                            ? 'Personal' 
                            : announcement.category || 'General'}
                      </span>
                    </div>
                  </div>
                  <div className="notification-item-indicator"></div>
                </div>
              ))}
              
              {unseenAnnouncements.length > 5 && (
                <div className="notification-more">
                  <span>+{unseenAnnouncements.length - 5} more announcements</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="notification-footer">
          <button className="notification-footer-btn secondary" onClick={handleClose}>
            Dismiss
          </button>
          <button className="notification-footer-btn primary" onClick={handleViewAll}>
            <span>View All</span>
            <IoChevronForward size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default UnseenAnnouncementsDialog;